/**
 * B2 (E-1.C.2-02): Reseller Revenue Dashboard
 *
 * Total MRR, margin per account, commission earned, payout history.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface RevenueMetrics {
  totalMrrCents: number;
  totalWholesaleCents: number;
  totalMarginCents: number;
  totalCommissionCents: number;
  pendingPayoutCents: number;
  activeAccounts: number;
  perClient: Array<{
    tenantId: string;
    tenantName: string;
    planName: string;
    revenueCents: number;
    wholesaleCents: number;
    marginCents: number;
  }>;
}

function MetricCard({ label, value, subtext, color }: { label: string; value: string; subtext?: string; color?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn("mt-1 text-2xl font-bold", color ?? "text-foreground")}>{value}</p>
      {subtext && <p className="mt-0.5 text-xs text-muted-foreground">{subtext}</p>}
    </div>
  );
}

export default function RevenueDashboardPage() {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<RevenueMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ data: RevenueMetrics }>("/platform/billing/revenue")
      .then((res) => setMetrics(res.data))
      .catch(() => setMetrics(null))
      .finally(() => setIsLoading(false));
  }, []);

  const fmt = (cents: number) => formatCurrency(cents, "EUR", { maximumFractionDigits: 0 });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />)}
        </div>
      </div>
    );
  }

  const m = metrics ?? { totalMrrCents: 0, totalWholesaleCents: 0, totalMarginCents: 0, totalCommissionCents: 0, pendingPayoutCents: 0, activeAccounts: 0, perClient: [] };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground">{t("billing.revenue.title")}</h2>

      <div className="grid gap-4 md:grid-cols-5">
        <MetricCard label={t("billing.revenue.mrr")} value={fmt(m.totalMrrCents)} />
        <MetricCard label={t("billing.revenue.wholesale")} value={fmt(m.totalWholesaleCents)} />
        <MetricCard label={t("billing.revenue.margin")} value={fmt(m.totalMarginCents)} color="text-[var(--color-success)]" />
        <MetricCard label={t("billing.revenue.commission")} value={fmt(m.totalCommissionCents)} />
        <MetricCard label={t("billing.revenue.pending")} value={fmt(m.pendingPayoutCents)} subtext={`${m.activeAccounts} ${t("billing.revenue.accounts")}`} />
      </div>

      {/* Per-client breakdown */}
      <div className="rounded-lg border border-border">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">{t("billing.revenue.perClient")}</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-4 py-2">{t("billing.revenue.client")}</th>
              <th className="px-4 py-2">{t("billing.revenue.plan")}</th>
              <th className="px-4 py-2 text-right">{t("billing.revenue.revenue")}</th>
              <th className="px-4 py-2 text-right">{t("billing.revenue.cost")}</th>
              <th className="px-4 py-2 text-right">{t("billing.revenue.profit")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {m.perClient.map((client) => (
              <tr key={client.tenantId} className="hover:bg-accent/30">
                <td className="px-4 py-2 text-foreground">{client.tenantName}</td>
                <td className="px-4 py-2 text-muted-foreground">{client.planName}</td>
                <td className="px-4 py-2 text-right">{fmt(client.revenueCents)}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{fmt(client.wholesaleCents)}</td>
                <td className="px-4 py-2 text-right font-medium text-[var(--color-success)]">{fmt(client.marginCents)}</td>
              </tr>
            ))}
            {m.perClient.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">{t("billing.revenue.noClients")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
